import { useReducer, useMemo, useCallback } from 'react';
import { VisibilityState } from '@tanstack/react-table';
import { HierarchicalNode } from './TSAdvancedHierarchicalTable';
import {
  groupColumnReducer,
  ColumnState,
  initialState,
  ACTION_TYPES,
} from './groupColumnReducer';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export interface UseGroupColumnManagerReturn {
  columnStates: Record<string, ColumnState>;
  columnVisibility: VisibilityState;
  isColumnHorizontal: (columnId: string) => boolean;
  toggleColumn: (columnId: string) => void;
  resetAllColumns: () => void;
  openAllColumns: () => void;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Converts an array of hierarchical nodes to a record format
 * @param nodes - Array of hierarchical nodes
 * @returns Record of nodes keyed by their ID
 */
const convertNodesToRecord = (
  nodes: HierarchicalNode[],
): Record<string, HierarchicalNode> => {
  const record: Record<string, HierarchicalNode> = {};
  nodes.forEach((node) => {
    record[node.id] = node;
  });
  return record;
};

/**
 * Generates column visibility state for TanStack Table
 * @param columnStates - Current column states
 * @param hiddenColumns - Optional array of column IDs to hide externally
 * @returns Visibility state object
 */
const generateColumnVisibility = (
  columnStates: Record<string, ColumnState>,
  hiddenColumns?: string[],
): VisibilityState => {
  const visibility: VisibilityState = {};
  const hiddenSet = new Set(hiddenColumns || []);
  
  Object.entries(columnStates).forEach(([columnId, columnState]) => {
    visibility[columnId] = columnState.isVisible && !hiddenSet.has(columnId);
  });
  return visibility;
};

// ============================================================================
// CUSTOM HOOK
// ============================================================================

/**
 * Custom hook for managing hierarchical group column navigation and state
 * Provides navigation-based column interactions with state management for group columns
 * 
 * @param columnContext - Array of root hierarchical nodes
 * @param defaultOpenColumns - Optional array of column IDs to open initially
 * @param hiddenColumns - Optional array of column IDs to hide externally (e.g., N/A columns)
 * @returns Object containing column states and control functions
 */
const useGroupColumnManager = (
  columnContext: HierarchicalNode[],
  defaultOpenColumns?: string[],
  hiddenColumns?: string[],
): UseGroupColumnManagerReturn => {
  const [state, dispatch] = useReducer(groupColumnReducer, initialState);

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  // Initialize columns when columnContext changes
  useMemo(() => {
    if (columnContext.length > 0) {
      const nodesRecord = convertNodesToRecord(columnContext);
      dispatch({
        type: ACTION_TYPES.INITIALIZE_COLUMNS,
        payload: {
          columnContext: nodesRecord,
          initialOpenColumns: defaultOpenColumns,
        },
      });
    }
  }, [columnContext, defaultOpenColumns]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  /**
   * Generates column visibility state for TanStack Table
   */
  const columnVisibility = useMemo(
    () => generateColumnVisibility(state.columnStates, hiddenColumns),
    [state.columnStates, hiddenColumns],
  );

  // ============================================================================
  // ACTION HANDLERS
  // ============================================================================

  /**
   * Determines if a column should be displayed horizontally
   * @param columnId - The column identifier (e.g., "Mathematics.Statistics")
   * @returns true if the column should be horizontal, false otherwise
   */
  const isColumnHorizontal = useCallback(
    (columnId: string): boolean => {
      const columnState = state.columnStates[columnId];
      return Boolean(columnState?.isHorizontal && columnState.isOpen);
    },
    [state.columnStates],
  );

  /**
   * Toggles a column's open/closed state with navigation behavior
   * @param columnId - The column identifier to toggle
   */
  const toggleColumn = useCallback(
    (columnId: string) => {
      const nodesRecord = convertNodesToRecord(columnContext);
      
      dispatch({
        type: ACTION_TYPES.COLUMN_CLICK,
        payload: {
          path: columnId.split('.'),
          columnContext: nodesRecord,
        },
      });
    },
    [columnContext],
  );

  /**
   * Resets all columns to their initial closed state
   */
  const resetAllColumns = useCallback(() => {
    dispatch({ type: ACTION_TYPES.RESET_ALL_COLUMNS });
  }, []);

  /**
   * Opens all columns in the hierarchy
   * Uses navigation behavior to open each closed column
   */
  const openAllColumns = useCallback(() => {
    const nodesRecord = convertNodesToRecord(columnContext);

    // Open all currently closed columns
    Object.entries(state.columnStates).forEach(([columnId, columnState]) => {
      if (!columnState.isOpen) {
        dispatch({
          type: ACTION_TYPES.COLUMN_CLICK,
          payload: {
            path: columnId.split('.'),
            columnContext: nodesRecord,
          },
        });
      }
    });
  }, [columnContext, state.columnStates]);

  // ============================================================================
  // RETURN VALUE
  // ============================================================================

  return {
    columnStates: state.columnStates,
    columnVisibility,
    isColumnHorizontal,
    toggleColumn,
    resetAllColumns,
    openAllColumns,
  };
};

export default useGroupColumnManager;